import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { RINGERS_API_URL } from '../config';
import MaintenancePage from './MaintenancePage';

const API_URL = RINGERS_API_URL;

const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phoneNumber;
};

function useTabTitle(title) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function MailingListPage() {
  const [groups, setGroups] = useState([]);
  const [persons, setPersons] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useTabTitle('Mailing List - Ringers');

  useEffect(() => {
    fetchGroups();
    fetchPersons();
  }, []);

  useEffect(() => {
    // Parse URL parameters to get selected groups
    const urlParams = new URLSearchParams(location.search);
    const groupIds = urlParams.getAll('groups').map(id => parseInt(id));
    setSelectedGroups(groupIds);
  }, [location.search]);

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (e) {
      console.error('Failed to fetch groups:', e);
    }
  };

  const fetchPersons = async () => {
    try {
      const response = await fetch(`${API_URL}/persons`);
      if (response.ok) {
        const data = await response.json();
        setPersons(data);
      }
    } catch (e) {
      console.error('Failed to fetch persons:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredPersons = persons.filter(person => {
    if (selectedGroups.length === 0) return false;
    return person.groups.some(group => selectedGroups.includes(group.id));
  }).sort((a, b) => {
    if (a.last_name !== b.last_name) {
      return a.last_name.localeCompare(b.last_name);
    }
    return a.first_name.localeCompare(b.first_name);
  });

  const selectedGroupNames = groups
    .filter(group => selectedGroups.includes(group.id))
    .map(group => group.name)
    .join(', ');

  return (
    <div style={{ 
      padding: '1.5rem 2rem', 
      maxWidth: '1400px', 
      margin: '0 auto'
    }} className="mailing-list-container">
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{selectedGroupNames || 'Mailing List'}</h1>
        <div className="no-print">
          <Link to="/ringers" style={{ marginRight: '1rem', padding: '10px 16px', backgroundColor: '#007bff', color: 'white', textDecoration: 'none', borderRadius: '5px' }}>
            Back to Ringers
          </Link>
          <button 
            onClick={() => window.print()}
            style={{ padding: '10px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            className="no-print"
          >
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '1rem'
        }}>
          {filteredPersons.map(person => (
            <div 
              key={person.id}
              data-person-entry
              style={{ 
                padding: '0.5rem 0',
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                {person.first_name} {person.last_name}
              </div>
              {person.address && (
                <div style={{ fontSize: '0.9rem', marginBottom: '0.1rem' }}>
                  {person.address}
                </div>
              )}
              {(person.city || person.zip_code) && (
                <div style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                  {person.city}{person.city && person.zip_code && ', '}{person.zip_code}
                </div>
              )}
              {person.phones && person.phones.length > 0 && (
                <div style={{ fontSize: '0.9rem', marginBottom: '0.1rem' }}>
                  {person.phones.map(phone => formatPhoneNumber(phone.phone_number)).join(', ')}
                </div>
              )}
              {person.emails && person.emails.length > 0 && (
                <div style={{ fontSize: '0.9rem' }}>
                  {person.emails.map(email => email.email_address).join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MainPage() {
  const navigate = useNavigate();
  const [persons, setPersons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [mailingGroups, setMailingGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateEmailLoading, setUpdateEmailLoading] = useState(false);
  const [updateEmailMsg, setUpdateEmailMsg] = useState('');

  const handlePersonClick = (personId) => {
    navigate(`/ringers/edit/${personId}`);
  };

  useTabTitle('Ringers');

  useEffect(() => {
    fetchPersons();
    fetchGroups();
  }, []);

  const handleUpdateEmailLists = async () => {
    setUpdateEmailLoading(true);
    setUpdateEmailMsg('');
    try {
      const response = await fetch(`${API_URL}/update-email-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        setUpdateEmailMsg(`Success! Updated ${result.results.length} email lists.`);
      } else {
        const error = await response.json();
        setUpdateEmailMsg(`Error: ${error.detail}`);
      }
    } catch (error) {
      setUpdateEmailMsg('Error: Failed to update email lists');
    } finally {
      setUpdateEmailLoading(false);
    }
  };

  const fetchPersons = async () => {
    try {
      const response = await fetch(`${API_URL}/persons`);
      if (response.ok) {
        const data = await response.json();
        setPersons(data.sort((a, b) => {
          if (a.last_name !== b.last_name) {
            return a.last_name.localeCompare(b.last_name);
          }
          return a.first_name.localeCompare(b.first_name);
        }));
      }
    } catch (e) {
      console.error('Failed to fetch persons:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (e) {
      console.error('Failed to fetch groups:', e);
    }
  };

  const searchPersons = async () => {
    if (!searchTerm.trim()) {
      fetchPersons();
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/persons/search?search_term=${encodeURIComponent(searchTerm)}`);
      if (response.ok) {
        const data = await response.json();
        setPersons(data.sort((a, b) => {
          if (a.last_name !== b.last_name) {
            return a.last_name.localeCompare(b.last_name);
          }
          return a.first_name.localeCompare(b.first_name);
        }));
      }
    } catch (e) {
      console.error('Failed to search persons:', e);
    }
  };

  const handleMailingGroupToggle = (groupId) => {
    setMailingGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const goToMailingList = () => {
    const params = new URLSearchParams();
    mailingGroups.forEach(id => params.append('groups', id));
    navigate(`/ringers/mailing-list?${params.toString()}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchPersons();
    }
  };

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Ringers</h1>
        <Link to="/" style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
          Back to Home
        </Link>
      </div>

      <div style={{ display: 'flex', gap: '2.5rem' }}>
        {/* Left column - Ringers list */}
        <div style={{ flex: 2, minWidth: '600px' }}>
          <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search ringers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                style={{ flex: 1, padding: '10px', paddingRight: searchTerm ? '35px' : '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    fetchPersons();
                  }}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <button 
              onClick={searchPersons}
              style={{ padding: '10px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Search
            </button>
            <Link to="/ringers/edit/new" style={{ padding: '10px 16px', backgroundColor: '#28a745', color: 'white', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
              Add New Ringer
            </Link>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <button 
              onClick={handleUpdateEmailLists}
              disabled={updateEmailLoading}
              style={{ 
                padding: '10px 16px', 
                backgroundColor: updateEmailLoading ? '#cccccc' : '#17a2b8', 
                color: updateEmailLoading ? '#666666' : 'white', 
                border: 'none', 
                borderRadius: '5px', 
                cursor: updateEmailLoading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {updateEmailLoading ? 'Updating...' : 'Update Email Lists'}
            </button>
            {updateEmailMsg && (
              <span style={{ marginLeft: '1rem', color: updateEmailMsg.includes('Error') ? '#dc3545' : '#28a745' }}>
                {updateEmailMsg}
              </span>
            )}
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {persons.map(person => (
                <div key={person.id} style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }} onClick={() => handlePersonClick(person.id)}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {person.first_name} {person.last_name}
                  </div>
                  {person.address && <div>{person.address}</div>}
                  {(person.city || person.zip_code) && (
                    <div>
                      {person.city}{person.city && person.zip_code && ', '}{person.zip_code}
                    </div>
                  )}
                  {person.phones && person.phones.length > 0 && (
                    <div>{person.phones.map(phone => formatPhoneNumber(phone.phone_number)).join(', ')}</div>
                  )}
                  {person.emails && person.emails.length > 0 && (
                    <div>{person.emails.map(email => email.email_address).join(', ')}</div>
                  )}
                  {person.groups && person.groups.length > 0 && (
                    <div>{person.groups.map(g => g.name).join(', ')}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column - Mailing list */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          <h2>Printable Mailing List</h2>
          <div style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '5px' }}>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Select Groups:</strong>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              {groups.map(group => (
                <label key={group.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={mailingGroups.includes(group.id)}
                    onChange={() => handleMailingGroupToggle(group.id)}
                    style={{ marginRight: '8px' }}
                  />
                  {group.name}
                </label>
              ))}
            </div>
            <button
              onClick={goToMailingList}
              style={{ 
                width: '100%',
                padding: '10px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '5px', 
                fontWeight: 'bold' 
              }}
              disabled={mailingGroups.length === 0}
            >
              View Mailing List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function PersonForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id !== 'new';
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    zip_code: ''
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [phones, setPhones] = useState([]);
  const [emails, setEmails] = useState([]);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [groups, setGroups] = useState([]);
  const [personGroups, setPersonGroups] = useState([]);
  const [initialPersonGroups, setInitialPersonGroups] = useState([]);
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  // Define fetchPerson before it's used in useEffect
  const fetchPerson = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/persons/${id}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch person: ${res.status}`);
      }
      const person = await res.json();
      const personData = {
        first_name: person.first_name,
        last_name: person.last_name,
        address: person.address || '',
        city: person.city || '',
        zip_code: person.zip_code || ''
      };
      setFormData(personData);
      setInitialFormData({ ...personData });
      setPhones(person.phones || []);
      setEmails(person.emails || []);
      setPersonGroups(person.groups || []);
      setInitialPersonGroups([...person.groups || []]);
      setHasUnsavedChanges(false);
      setError('');
    } catch (err) {
      console.error('Error fetching person:', err);
      setError(`Failed to fetch person: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/groups`);
      setGroups(await res.json());
    } catch (err) {
      setError('Failed to fetch groups');
    }
  };

  // Update document title
  useEffect(() => {
    const title = isEdit && formData.first_name && formData.last_name 
      ? `${formData.first_name} ${formData.last_name} - Ringers`
      : 'Ringers';
    document.title = title;
  }, [isEdit, formData.first_name, formData.last_name]);

  useEffect(() => {
    setLoading(true);
    setError('');
    
    if (isEdit) {
      fetchPerson();
    } else {
      // Initialize for new person
      const initialData = {
        first_name: '',
        last_name: '',
        address: '',
        city: '',
        zip_code: ''
      };
      setFormData(initialData);
      setInitialFormData(initialData);
      setPhones([]);
      setEmails([]);
      setPersonGroups([]);
      setInitialPersonGroups([]);
      setHasUnsavedChanges(false);
      setLoading(false);
    }
    fetchGroups();
  }, [id, isEdit, fetchPerson]); // Include fetchPerson in dependencies

  // Helper function to get detailed list of unsaved changes
  const getUnsavedChangesDetails = () => {
    const details = [];
    
    // Check form data changes
    if (Object.keys(formData).length > 0 && Object.keys(initialFormData).length > 0) {
      const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      if (formChanged) {
        // Check which specific fields changed
        const changedFields = [];
        if (formData.first_name !== initialFormData.first_name) changedFields.push('First Name');
        if (formData.last_name !== initialFormData.last_name) changedFields.push('Last Name');
        if (formData.address !== initialFormData.address) changedFields.push('Address');
        if (formData.city !== initialFormData.city) changedFields.push('City');
        if (formData.zip_code !== initialFormData.zip_code) changedFields.push('Zip Code');
        if (changedFields.length > 0) {
          details.push(`Form fields: ${changedFields.join(', ')}`);
        }
      }
    }
    
    // Check for unadded phone/email
    if (newPhone.trim().length > 0) {
      details.push('unadded phone number');
    }
    if (newEmail.trim().length > 0) {
      details.push('unadded email address');
    }
    
    // Check if there are phones, emails, or groups added for new person
    if (!isEdit) {
      if (phones.length > 0) details.push('phone numbers');
      if (emails.length > 0) details.push('email addresses');
      if (personGroups.length > 0) details.push('group memberships');
    } else {
      // For existing person, check if groups changed
      const groupsChanged = JSON.stringify(personGroups.map(g => g.id).sort()) !== 
                           JSON.stringify(initialPersonGroups.map(g => g.id).sort());
      if (groupsChanged) details.push('group memberships');
    }
    
    return details;
  };

  // Track unsaved changes
  useEffect(() => {
    let hasChanges = false;
    
    // Check form data changes
    if (Object.keys(formData).length > 0 && Object.keys(initialFormData).length > 0) {
      hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    }
    
    // Check if there are phones, emails, or groups added for new person
    if (!isEdit) {
      hasChanges = hasChanges || phones.length > 0 || emails.length > 0 || personGroups.length > 0;
    } else {
      // For existing person, check if groups changed
      const groupsChanged = JSON.stringify(personGroups.map(g => g.id).sort()) !== 
                           JSON.stringify(initialPersonGroups.map(g => g.id).sort());
      hasChanges = hasChanges || groupsChanged;
    }
    
    // Check if there's unsaved phone or email input (user typed but didn't click Add)
    hasChanges = hasChanges || (newPhone.trim().length > 0) || (newEmail.trim().length > 0);
    
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialFormData, phones, emails, personGroups, initialPersonGroups, isEdit, newPhone, newEmail]);

  // Warn before leaving page with unsaved changes (handles tab close/refresh)
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      const details = getUnsavedChangesDetails();
      const message = details.length > 0 
        ? `You have unsaved changes (${details.join(', ')}). Are you sure you want to leave?`
        : 'You have unsaved changes. Are you sure you want to leave?';
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, formData, initialFormData, newPhone, newEmail, phones, emails, personGroups, initialPersonGroups, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for unadded phone or email before submitting
    const unsavedDetails = [];
    if (newPhone.trim().length > 0) {
      unsavedDetails.push('phone number');
    }
    if (newEmail.trim().length > 0) {
      unsavedDetails.push('email address');
    }
    
    if (unsavedDetails.length > 0) {
      const message = `You have entered a ${unsavedDetails.join(' and ')} but haven't clicked "Add". Please add ${unsavedDetails.length > 1 ? 'them' : 'it'} before saving, or clear the field${unsavedDetails.length > 1 ? 's' : ''}.`;
      setError(message);
      return;
    }
    
    try {
      const url = isEdit ? `${API_URL}/persons/${id}` : `${API_URL}/persons`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const savedPerson = await res.json();
        
        if (!isEdit) {
          // For new person, save phones, emails, and groups
          const personId = savedPerson.id;
          
          // Add phones
          for (const phone of phones) {
            await fetch(`${API_URL}/persons/${personId}/phones`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone_number: phone.phone_number })
            });
          }
          
          // Add emails
          for (const email of emails) {
            await fetch(`${API_URL}/persons/${personId}/emails`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email_address: email.email_address })
            });
          }
          
          // Add groups
          for (const group of personGroups) {
            await fetch(`${API_URL}/persons/${personId}/groups/${group.id}`, {
              method: 'POST'
            });
          }
          
          setHasUnsavedChanges(false);
          // Redirect to the edit page for the newly created person
          // Use replace to avoid back button issues
          navigate(`/ringers/edit/${personId}`, { replace: true });
        } else {
          setHasUnsavedChanges(false);
          navigate('/ringers');
        }
      } else {
        setError('Failed to save person');
      }
    } catch (err) {
      setError('Failed to save person');
    }
  };

  const handleAddPhone = async () => {
    if (!newPhone.trim()) return;
    try {
      if (isEdit) {
        const res = await fetch(`${API_URL}/persons/${id}/phones`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: newPhone })
        });
        if (res.ok) {
          // Refresh person data to ensure consistency
          await fetchPerson();
          setNewPhone('');
          setError(''); // Clear any previous errors
        } else {
          const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
          setError(`Failed to add phone: ${errorData.detail || 'Unknown error'}`);
        }
      } else {
        // For new person, just add to local state
        const tempPhone = { id: Date.now(), phone_number: newPhone };
        setPhones([...phones, tempPhone]);
        setNewPhone('');
      }
    } catch (err) {
      console.error('Error adding phone:', err);
      setError(`Failed to add phone: ${err.message}`);
    }
  };

  const handleRemovePhone = async (phoneId) => {
    try {
      if (isEdit) {
        const res = await fetch(`${API_URL}/phones/${phoneId}`, { method: 'DELETE' });
        if (res.ok) {
          setPhones(phones.filter(p => p.id !== phoneId));
        } else {
          setError('Failed to remove phone');
        }
      } else {
        // For new person, just remove from local state
        setPhones(phones.filter(p => p.id !== phoneId));
      }
    } catch (err) {
      setError('Failed to remove phone');
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    try {
      if (isEdit) {
        const res = await fetch(`${API_URL}/persons/${id}/emails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email_address: newEmail })
        });
        if (res.ok) {
          const newEmailObj = await res.json();
          setEmails([...emails, newEmailObj]);
          setNewEmail('');
        } else {
          setError('Failed to add email');
        }
      } else {
        // For new person, just add to local state
        const tempEmail = { id: Date.now(), email_address: newEmail };
        setEmails([...emails, tempEmail]);
        setNewEmail('');
      }
    } catch (err) {
      setError('Failed to add email');
    }
  };

  const handleRemoveEmail = async (emailId) => {
    try {
      if (isEdit) {
        const res = await fetch(`${API_URL}/emails/${emailId}`, { method: 'DELETE' });
        if (res.ok) {
          setEmails(emails.filter(e => e.id !== emailId));
        } else {
          setError('Failed to remove email');
        }
      } else {
        // For new person, just remove from local state
        setEmails(emails.filter(e => e.id !== emailId));
      }
    } catch (err) {
      setError('Failed to remove email');
    }
  };

  const handleGroupToggle = async (groupId) => {
    try {
      const isInGroup = personGroups.some(g => g.id === groupId);
      if (isInGroup) {
        if (isEdit) {
          await fetch(`${API_URL}/persons/${id}/groups/${groupId}`, { method: 'DELETE' });
        }
        setPersonGroups(personGroups.filter(g => g.id !== groupId));
      } else {
        if (isEdit) {
          await fetch(`${API_URL}/persons/${id}/groups/${groupId}`, { method: 'POST' });
        }
        const group = groups.find(g => g.id === groupId);
        setPersonGroups([...personGroups, group]);
      }
    } catch (err) {
      setError('Failed to update group membership');
    }
  };

  const handleDeletePerson = async () => {
    if (!window.confirm('Are you sure you want to delete this ringer? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/persons/${id}`, { method: 'DELETE' });
      if (response.ok) {
        navigate('/ringers');
      } else {
        setError('Failed to delete ringer');
      }
    } catch (err) {
      setError('Failed to delete ringer');
    }
  };

  const pageTitle = isEdit && formData.first_name && formData.last_name 
    ? `${formData.first_name} ${formData.last_name}`
    : 'Add New Ringer';

  if (loading) {
    return (
      <div style={{ padding: '1.5rem 2rem', maxWidth: '1000px', margin: '0 auto' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>{pageTitle}</h1>
        <Link 
          to="/ringers" 
          onClick={(e) => {
            if (hasUnsavedChanges) {
              const details = getUnsavedChangesDetails();
              const message = details.length > 0 
                ? `You have unsaved changes (${details.join(', ')}). Are you sure you want to leave?`
                : 'You have unsaved changes. Are you sure you want to leave?';
              if (!window.confirm(message)) {
                e.preventDefault();
              }
            }
          }}
          style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}
        >
          Back to Ringers
        </Link>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

      <form id="person-form" onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>First Name:</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Last Name:</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Address:</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>City:</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              autoComplete="off"
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Zip Code:</label>
            <input
              type="text"
              value={formData.zip_code}
              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              autoComplete="off"
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>
        </div>
      </form>

      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>Phones</h2>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="tel"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="Add new phone number"
            style={{ width: '200px', padding: '8px', marginRight: '8px' }}
          />
          <button onClick={handleAddPhone} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Add Phone</button>
        </div>
        <ul>
          {phones.map(phone => (
            <li key={phone.id} style={{ marginBottom: '0.5rem' }}>
              {formatPhoneNumber(phone.phone_number)}
              <button 
                onClick={() => handleRemovePhone(phone.id)}
                style={{ marginLeft: '8px', padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>Emails</h2>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Add new email address"
            style={{ width: '200px', padding: '8px', marginRight: '8px' }}
          />
          <button onClick={handleAddEmail} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Add Email</button>
        </div>
        <ul>
          {emails.map(email => (
            <li key={email.id} style={{ marginBottom: '0.5rem' }}>
              {email.email_address}
              <button 
                onClick={() => handleRemoveEmail(email.id)}
                style={{ marginLeft: '8px', padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>Groups</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {groups.map(group => (
            <label key={group.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={personGroups.some(g => g.id === group.id)}
                onChange={() => handleGroupToggle(group.id)}
                style={{ marginRight: '8px' }}
              />
              {group.name}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
        <button 
          type="submit"
          form="person-form"
          style={{ 
            padding: '12px 24px', 
            backgroundColor: '#28a745', 
            color: 'white', 
            border: 'none', 
            borderRadius: '5px', 
            fontSize: '16px', 
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          {isEdit ? 'Update Ringer' : 'Create Ringer'}
        </button>
        {isEdit && (
          <button 
            onClick={handleDeletePerson}
            style={{ 
              padding: '12px 24px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Delete Ringer
          </button>
        )}
      </div>
    </div>
  );
}

function RingersApp() {
  return (
    <Routes>
      <Route path="/" element={<MainPage />} />
      <Route path="/edit/:id" element={<PersonForm />} />
      <Route path="/mailing-list" element={<MailingListPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
    </Routes>
  );
}

export default RingersApp; 